// src/App.tsx
import { useContext } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Box } from "@mui/material";
import { DarkModeContext } from "./context/darkModeContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { PermissionsProvider, usePermissions } from "./context/PermissionsContext";
import { UserProfileProvider } from "./context/UserProfileContext";
import { DarkModeContextProvider } from "./context/darkModeContext";
import { es } from "date-fns/locale";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import ProtectedRoute from "./components/common/ProtectedRoute/ProtectedRoute";
import AppThemeProvider from "./theme/AppThemeProvider";

// Pages principales
import Home from "./pages/home/Home";
import Login from "./pages/login/Login";
import Mantenimiento from "./pages/mantenimiento/Mantenimiento";
import Almacen from "./pages/almacen/Almacen";
import Compras from "./pages/compras/Compras";
import DevolucionesCompra from "./pages/devolucionesCompra/DevolucionesCompra";
import DevolucionesVenta from "./pages/devolucionesVenta/DevolucionVenta";
import Facturas from "./pages/facturas/Facturas";
import Usuarios from "./pages/usuarios/Usuarios";
import PerfilUsuario from "./pages/usuarios/PerfilUsuario"; // ← AGREGAR ESTA LÍNEA
import Ventas from "./pages/ventas/Ventas";
import Areas from "./pages/administracion/areas/Areas";
import MaintenanceModule from "./pages/mantenimiento/MaintenanceModule";
import Administracion from "./pages/administracion/Administracion";
import Cargos from "./pages/administracion/cargos/Cargos";
import Empresas from "./pages/administracion/empresa/Empresas";
import Sucursales from "./pages/administracion/sucursales/Sucursal";
import Impuestos from "./pages/administracion/impuestos/Impuestos";
import Proveedores from "./pages/administracion/proveedores/Proveedores";
import Empleados from "./pages/administracion/empleados/Empleados";
import Clientes from "./pages/administracion/clientes/Clientes";
import EmpleadoDetailView from "./pages/administracion/empleados/EmpleadoDetailView";
import ClienteDetailView from "./pages/administracion/clientes/ClienteDetailView";
import Accesos from "./pages/administracion/accesos/Accesos";
import { ConfiguracionSmtpPage } from "./pages/administracion/configuracion-email/ConfiguracionSmtp";
import FacturacionElectronica from "./pages/administracion/facturacion-electronica/FacturacionElectronica";

import ChatIA from "./components/common/ChatIA/ChatIA";

// Styles
import "./style/dark.scss";
import Articulos from "./pages/almacen/Articulos/Articulos";
import Inventarios from "./pages/almacen/inventarios/Inventarios";
import CompraForm from "./components/module/compras/CompraForm";
import PurchaseOrderDetails from "./components/module/compras/PurchaseOrderDetails";
import PurchaseOrderWrapper from "./components/module/compras/PurchaseOrderWrapper";
import DevolucionCompraForm from "./pages/devolucionesCompra/DevolucionCompraForm";
import { VentasList } from "./components/module/ventas/VentasList";
import { VentaDetailPage } from "./components/module/ventas/VentaDetailPage";
import CompletarVentaPendienteWrapper from "./components/module/ventas/CompletarVentaPendienteWrapper";
import Cotizaciones from "./pages/cotizaciones/Cotizaciones";
import { CotizacionesList } from "./components/module/cotizaciones/CotizacionesList";
import { CotizacionDetailPage } from "./components/module/cotizaciones/CotizacionDetailPage";
import Reportes from "./pages/reportes/Reportes";
import NoAutorizado from "./pages/no-autorizado/NoAutorizado";

// Componente para la ruta raíz: muestra el dashboard si tiene acceso,
// o redirige al primer módulo disponible sin pasar por /no-autorizado.
const HomeRoute: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const { hasModuleAccess, modulos, isPermissionsLoaded } = usePermissions();

  if (isLoading || (isAuthenticated && !isPermissionsLoaded)) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <div>Cargando...</div>
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (hasModuleAccess("/")) {
    return <Home />;
  }

  // Sin acceso al dashboard: ir al primer módulo disponible
  if (modulos.length > 0) {
    return <Navigate to={modulos[0]} replace />;
  }

  // Sin ningún módulo asignado
  return <Navigate to="/no-autorizado" replace />;
};

const AppRoutes: React.FC = () => {
  const { darkMode } = useContext(DarkModeContext);
  const { isLoading, isAuthenticated, user } = useAuth();

  if (isLoading) {
    return <div>Cargando...</div>;
  }

  return (
    <div className={darkMode ? "app dark" : "app"}>
      {isAuthenticated && <ChatIA />}
      <Routes>
        {/* Ruta raíz — muestra Home si tiene acceso, o redirige al primer módulo disponible */}
        <Route path="/" element={<HomeRoute />} />
        
        {/* Ruta de login */}
        <Route
          path="/login"
          element={isAuthenticated ? <Navigate to="/" replace /> : <Login />}
        />
        
        {/* Rutas de Administración */}
        <Route path="/administracion">
          <Route
            index
            element={
              <ProtectedRoute module="/administracion">
                <Administracion />
              </ProtectedRoute>
            }
          />
          <Route
            path="areas"
            element={
              <ProtectedRoute module="/administracion">
                <Areas />
              </ProtectedRoute>
            }
          />
          <Route
            path="facturacion-electronica"
            element={
              <ProtectedRoute module="/administracion/facturacion-electronica">
                <FacturacionElectronica />
              </ProtectedRoute>
            }
          />
          <Route
            path="cargos"
            element={
              <ProtectedRoute module="/administracion">
                <Cargos />
              </ProtectedRoute>
            }
          />
          <Route
            path="empresas"
            element={
              <ProtectedRoute module="/administracion">
                <Empresas />
              </ProtectedRoute>
            }
          />
          <Route
            path="sucursales"
            element={
              <ProtectedRoute module="/administracion">
                <Sucursales />
              </ProtectedRoute>
            }
          />
          <Route
            path="impuestos"
            element={
              <ProtectedRoute module="/administracion">
                <Impuestos />
              </ProtectedRoute>
            }
          />
          <Route
            path="proveedores"
            element={
              <ProtectedRoute module="/administracion">
                <Proveedores />
              </ProtectedRoute>
            }
          />
          <Route
            path="clientes"
            element={
              <ProtectedRoute module="/administracion">
                <Clientes />
              </ProtectedRoute>
            }
          />
          <Route
            path="clientes/view/:id"
            element={
              <ProtectedRoute module="/administracion">
                <ClienteDetailView />
              </ProtectedRoute>
            }
          />
          <Route
            path="empleados"
            element={
              <ProtectedRoute module="/administracion">
                <Empleados />
              </ProtectedRoute>
            }
          />
          <Route
            path="empleados/view/:id"
            element={
              <ProtectedRoute module="/administracion">
                <EmpleadoDetailView />
              </ProtectedRoute>
            }
          />
          <Route
            path="accesos"
            element={
              <ProtectedRoute module="/administracion">
                <Accesos />
              </ProtectedRoute>
            }
          />
          <Route
            path="configuracion-email"
            element={
              <ProtectedRoute module="/administracion">
                <ConfiguracionSmtpPage />
              </ProtectedRoute>
            }
          />
        </Route>
        
        {/* Rutas de Mantenimiento */}
        <Route path="/mantenimiento">
          <Route
            index
            element={
              <ProtectedRoute module="/mantenimiento">
                <Mantenimiento />
              </ProtectedRoute>
            }
          />
          <Route
            path=":submodule"
            element={
              <ProtectedRoute module="/mantenimiento">
                <MaintenanceModule />
              </ProtectedRoute>
            }
          />
        </Route>
        
        {/* Rutas de Almacén */}
        <Route path="/almacen">
          <Route
            index
            element={
              <ProtectedRoute module="/almacen">
                <Almacen />
              </ProtectedRoute>
            }
          />
          <Route
            path="articulos"
            element={
              <ProtectedRoute module="/almacen">
                <Articulos />
              </ProtectedRoute>
            }
          />
          <Route
            path="inventarios"
            element={
              <ProtectedRoute module="/almacen">
                <Inventarios />
              </ProtectedRoute>
            }
          />
        </Route>

        {/* Rutas de Compras */}
        <Route path="/compras">
          <Route
            index
            element={
              <ProtectedRoute module="/compras">
                <Compras />
              </ProtectedRoute>
            }
          />
          <Route
            path="new"
            element={
              <ProtectedRoute module="/compras">
                <CompraForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="edit/:id"
            element={
              <ProtectedRoute module="/compras">
                <CompraForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="detalle/:id"
            element={
              <ProtectedRoute module="/compras">
                <PurchaseOrderWrapper />
              </ProtectedRoute>
            }
          />
        </Route>
        
        {/* Rutas de Devoluciones Compra */}
        <Route path="/devoluciones-compra">
          <Route
            index
            element={
              <ProtectedRoute module="/devoluciones-compra">
                <DevolucionesCompra />
              </ProtectedRoute>
            }
          />
          <Route
            path="new"
            element={
              <ProtectedRoute module="/devoluciones-compra">
                <DevolucionCompraForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="edit/:id"
            element={
              <ProtectedRoute module="/devoluciones-compra">
                <DevolucionCompraForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="detalle/:id"
            element={
              <ProtectedRoute module="/devoluciones-compra">
                <DevolucionesCompra />
              </ProtectedRoute>
            }
          />
        </Route>
        
        {/* Ruta de Devoluciones Venta */}
        <Route
          path="/devoluciones-venta"
          element={
            <ProtectedRoute module="/devoluciones-venta">
              <DevolucionesVenta />
            </ProtectedRoute>
          }
        />
        
        {/* Ruta de Facturas */}
        <Route
          path="/facturas"
          element={
            <ProtectedRoute module="/facturas">
              <Facturas />
            </ProtectedRoute>
          }
        />
        
        {/* Rutas de Usuarios */}
        <Route path="/usuarios">
          <Route
            index
            element={
              <ProtectedRoute module="/usuarios">
                <Usuarios />
              </ProtectedRoute>
            }
          />
        </Route>
        
        {/* Rutas de Ventas */}
        <Route path="/ventas">
          <Route
            index
            element={
              <ProtectedRoute module="/ventas">
                <VentasList />
              </ProtectedRoute>
            }
          />
          <Route
            path="new"
            element={
              <ProtectedRoute module="/ventas">
                <Ventas />
              </ProtectedRoute>
            }
          />
          <Route
            path="detail/:id"
            element={
              <ProtectedRoute module="/ventas">
                <VentaDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="continue"
            element={
              <ProtectedRoute module="/ventas">
                <CompletarVentaPendienteWrapper />
              </ProtectedRoute>
            }
          />
        </Route>
        
        {/* Rutas de Cotizaciones */}
        <Route path="/cotizaciones">
          <Route
            index
            element={
              <ProtectedRoute module="/cotizaciones">
                <CotizacionesList />
              </ProtectedRoute>
            }
          />
          <Route
            path="new"
            element={
              <ProtectedRoute module="/cotizaciones">
                <Cotizaciones />
              </ProtectedRoute>
            }
          />
          <Route
            path="detail/:id"
            element={
              <ProtectedRoute module="/cotizaciones">
                <CotizacionDetailPage />
              </ProtectedRoute>
            }
          />
        </Route>

        {/* Rutas de Reportes */}
        <Route
          path="/reportes"
          element={
            <ProtectedRoute module="/reportes">
              <Reportes />
            </ProtectedRoute>
          }
        />
        
        {/* Perfil de usuario — accesible para cualquier usuario autenticado */}
        <Route
          path="/perfil/:id"
          element={
            <ProtectedRoute>
              <PerfilUsuario />
            </ProtectedRoute>
          }
        />

        {/* Ruta de acceso no autorizado */}
        <Route path="/no-autorizado" element={<NoAutorizado />} />

        {/* Ruta para páginas no encontradas */}
        <Route
          path="*"
          element={<Navigate to={isAuthenticated ? "/" : "/login"} replace />}
        />
      </Routes>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <DarkModeContextProvider>
        <AppThemeProvider>
          <PermissionsProvider>
            <UserProfileProvider>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
                <BrowserRouter>
                  <AppRoutes />
                </BrowserRouter>
              </LocalizationProvider>
            </UserProfileProvider>
          </PermissionsProvider>
        </AppThemeProvider>
      </DarkModeContextProvider>
    </AuthProvider>
  );
};

export default App;